import React, { useState, useMemo } from 'react';
import moment from 'moment-timezone';

/**
 * TimezoneSelector - Comprehensive timezone selector with search
 * Features:
 * - Full IANA timezone database (595+ timezones)
 * - Search/filter by name
 * - Grouped by region
 * - Shows UTC offset
 * - Auto-detects user's timezone
 */
function TimezoneSelector({ value, onChange, style }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Get all timezones grouped by region
  const timezonesByRegion = useMemo(() => {
    const zones = moment.tz.names();
    const grouped = {};

    zones.forEach(zone => {
      // Skip deprecated zones
      if (zone.includes('Etc/') || zone.includes('GMT') || zone.includes('UTC')) {
        return;
      }

      const parts = zone.split('/');
      if (parts.length < 2) return;

      const region = parts[0];
      if (!grouped[region]) {
        grouped[region] = [];
      }

      // Get UTC offset
      const offset = moment.tz(zone).format('Z');
      
      grouped[region].push({
        value: zone,
        label: zone.replace(/_/g, ' '),
        utcOffset: offset,
        region
      });
    });

    // Sort timezones within each region
    Object.keys(grouped).forEach(region => {
      grouped[region].sort((a, b) => a.label.localeCompare(b.label));
    });

    return grouped;
  }, []);

  // Get popular timezones for quick access
  const popularTimezones = useMemo(() => [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Europe/Athens',
    'Asia/Dubai',
    'Asia/Kolkata',
    'Asia/Shanghai',
    'Asia/Tokyo',
    'Australia/Sydney',
    'Pacific/Auckland'
  ], []);

  // Filter timezones based on search
  const filteredTimezones = useMemo(() => {
    if (!searchTerm) return timezonesByRegion;

    const term = searchTerm.toLowerCase();
    const filtered = {};

    Object.keys(timezonesByRegion).forEach(region => {
      const matchingZones = timezonesByRegion[region].filter(tz =>
        tz.label.toLowerCase().includes(term) ||
        tz.value.toLowerCase().includes(term) ||
        tz.region.toLowerCase().includes(term)
      );

      if (matchingZones.length > 0) {
        filtered[region] = matchingZones;
      }
    });

    return filtered;
  }, [searchTerm, timezonesByRegion]);

  // Get display name for selected timezone
  const getSelectedDisplay = () => {
    if (!value) return 'Select timezone...';
    
    const offset = moment.tz(value).format('Z');
    return `${value.replace(/_/g, ' ')} (UTC${offset})`;
  };

  // Detect user's timezone
  const detectTimezone = () => {
    const detected = moment.tz.guess();
    onChange(detected);
  };

  return (
    <div style={{ position: 'relative', ...style }}>
      {/* Selected value display */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '0.75rem',
          borderRadius: '6px',
          border: '1px solid #cbd5e0',
          fontSize: '1rem',
          background: 'white',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <span>{getSelectedDisplay()}</span>
        <span style={{ color: '#718096' }}>▼</span>
      </div>

      {/* Dropdown panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setIsOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
          />

          {/* Dropdown content */}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '0.25rem',
              background: 'white',
              border: '1px solid #cbd5e0',
              borderRadius: '8px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              maxHeight: '400px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 1000
            }}
          >
            {/* Search box */}
            <div style={{ padding: '0.75rem', borderBottom: '1px solid #e2e8f0' }}>
              <input
                type="text"
                placeholder="Search timezones..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #cbd5e0',
                  fontSize: '0.9rem'
                }}
              />
            </div>

            {/* Auto-detect button */}
            <div style={{ padding: '0.5rem', borderBottom: '1px solid #e2e8f0' }}>
              <button
                onClick={() => {
                  detectTimezone();
                  setIsOpen(false);
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: 'none',
                  background: '#edf2f7',
                  color: 'var(--color-primary)',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: '500'
                }}
              >
                🌐 Auto-detect my timezone
              </button>
            </div>

            {/* Popular timezones (only when no search) */}
            {!searchTerm && (
              <div style={{ padding: '0.5rem', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ 
                  padding: '0.5rem', 
                  fontSize: '0.75rem', 
                  fontWeight: '600', 
                  color: '#718096',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  ⭐ Popular Timezones
                </div>
                {popularTimezones.map(tz => {
                  const offset = moment.tz(tz).format('Z');
                  return (
                    <div
                      key={tz}
                      onClick={() => {
                        onChange(tz);
                        setIsOpen(false);
                      }}
                      style={{
                        padding: '0.5rem 0.75rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        background: value === tz ? '#edf2f7' : 'transparent',
                        borderRadius: '4px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#f7fafc'}
                      onMouseLeave={(e) => e.target.style.background = value === tz ? '#edf2f7' : 'transparent'}
                    >
                      <span>{tz.replace(/_/g, ' ')}</span>
                      <span style={{ fontSize: '0.8rem', color: '#718096' }}>UTC{offset}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* All timezones grouped by region */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {Object.keys(filteredTimezones).length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#718096' }}>
                  No timezones found for "{searchTerm}"
                </div>
              ) : (
                Object.keys(filteredTimezones).sort().map(region => (
                  <div key={region} style={{ padding: '0.5rem' }}>
                    <div style={{ 
                      padding: '0.5rem', 
                      fontSize: '0.75rem', 
                      fontWeight: '600', 
                      color: '#718096',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      position: 'sticky',
                      top: 0,
                      background: 'white',
                      zIndex: 1
                    }}>
                      {region}
                    </div>
                    {filteredTimezones[region].map(tz => (
                      <div
                        key={tz.value}
                        onClick={() => {
                          onChange(tz.value);
                          setIsOpen(false);
                        }}
                        style={{
                          padding: '0.5rem 0.75rem',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          background: value === tz.value ? '#edf2f7' : 'transparent',
                          borderRadius: '4px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#f7fafc'}
                        onMouseLeave={(e) => e.target.style.background = value === tz.value ? '#edf2f7' : 'transparent'}
                      >
                        <span>{tz.label.split('/').slice(1).join(' - ')}</span>
                        <span style={{ fontSize: '0.8rem', color: '#718096' }}>UTC{tz.utcOffset}</span>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>

            {/* Results count */}
            {searchTerm && (
              <div style={{ 
                padding: '0.5rem 0.75rem', 
                borderTop: '1px solid #e2e8f0',
                fontSize: '0.75rem',
                color: '#718096',
                background: '#f7fafc'
              }}>
                {Object.values(filteredTimezones).reduce((sum, zones) => sum + zones.length, 0)} timezones found
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default TimezoneSelector;
